import { Box, Chip, MenuItem, Paper, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { ServerDataGrid } from '../../shared/data/ServerDataGrid';
import type { PageResult } from '../../shared/data/types';
import { countActiveFilterValues } from '../../shared/filter/activeFilterCount';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { formatMoney, type MoneyLanguage } from '../../shared/format/money';
import { formatTimestamp } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { toPageError } from '../partners/PartnerListPage';
import { defaultMemberListQuery, memberRepositoryToken } from './repository';
import type { MemberFilters, MemberListQuery, MemberSummary } from './types';

export function MemberListPage({ dataSource }: { readonly dataSource: DataSource }) {
  const repository = useRepository(memberRepositoryToken);
  const [searchParams] = useSearchParams();
  const { i18n, t } = useTranslation();
  const language: MoneyLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [query, setQuery] = useState(() => initialMemberListQuery(searchParams));
  const [refreshKey, setRefreshKey] = useState(0);
  const resource = useResource(
    (signal) => repository.list(query, signal),
    [query, refreshKey, repository],
    (page) => page.items.length === 0,
  );
  const columns = useMemo<readonly GridColDef<MemberSummary>[]>(
    () => [
      {
        field: 'username',
        headerName: t('members.fields.username'),
        minWidth: 170,
        renderCell: ({ row }) => <Link to={`/members/${row.id}`}>{row.username ?? row.id}</Link>,
      },
      {
        field: 'partnerUserId',
        headerName: t('members.fields.partnerUserId'),
        minWidth: 140,
        sortable: false,
        valueGetter: (_value, row) => row.partnerUserId ?? t('common.notAvailable'),
      },
      {
        field: 'partnerId',
        headerName: t('members.fields.partner'),
        minWidth: 180,
        sortable: false,
        renderCell: ({ row }) => <Link to={`/partners/${row.partnerId}`}>{row.partnerId}</Link>,
      },
      {
        field: 'agent',
        headerName: t('members.fields.agent'),
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }) =>
          row.currentAssignment === null ? (
            t('common.notAvailable')
          ) : (
            <Link to={`/agents/${row.currentAssignment.agent.id}`}>
              {row.currentAssignment.agent.code}
            </Link>
          ),
      },
      {
        field: 'upline',
        headerName: t('members.fields.directUpline'),
        minWidth: 170,
        sortable: false,
        renderCell: ({ row }) => {
          const upline = row.currentAssignment?.upline;
          return upline === null || upline === undefined ? (
            t('common.notAvailable')
          ) : (
            <Link to={`/members/${upline.memberId}`}>{upline.username ?? upline.memberId}</Link>
          );
        },
      },
      {
        field: 'activeReferralCodes',
        headerName: t('members.fields.referralCode'),
        minWidth: 210,
        sortable: false,
        valueGetter: (_value, row) =>
          row.activeReferralCodes.join(', ') || t('common.notAvailable'),
      },
      {
        field: 'status',
        headerName: t('members.fields.status'),
        minWidth: 120,
        renderCell: ({ row }) => <MemberStatusChip status={row.status} />,
      },
      { field: 'memberType', headerName: t('members.fields.type'), minWidth: 110 },
      {
        field: 'registeredAt',
        headerName: t('members.fields.registeredAt'),
        minWidth: 190,
        valueFormatter: (value: string) => formatTimestamp(value, 'Asia/Bangkok', language).display,
      },
      {
        field: 'directDownlineCount',
        headerName: t('members.fields.downlines'),
        minWidth: 120,
        sortable: false,
      },
      {
        field: 'wallet',
        headerName: t('members.fields.wallet'),
        minWidth: 210,
        sortable: false,
        renderCell: ({ row }) => {
          const wallet = row.defaultWallet;
          if (wallet === null) return t('common.notAvailable');
          return (
            <Box
              component="span"
              sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.35 }}
            >
              <span>
                {t('members.fields.available')}:{' '}
                {formatMoney(wallet.availableBalance, wallet.currencyCode, language)}
              </span>
              <span>
                {t('members.fields.reserved')}:{' '}
                {formatMoney(wallet.reservedBalance, wallet.currencyCode, language)}
              </span>
            </Box>
          );
        },
      },
    ],
    [language, t],
  );

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={<DataSourceBadge source={dataSource} />}
        subtitle={t('members.subtitle')}
        title={t('members.title')}
      />
      <MemberFiltersForm
        onApply={(filters) => setQuery((current) => ({ ...current, page: 1, filters }))}
        value={query.filters}
      />
      <Paper component="aside" variant="outlined" sx={{ mb: 2, p: 1.5 }}>
        {t('members.contractNote')}
      </Paper>
      <PageState
        emptyMessage={t('members.empty')}
        error={toPageError(resource.error, t('members.errors.load'))}
        onRetry={() => setRefreshKey((key) => key + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <MemberGrid
            columns={columns}
            onQueryChange={setQuery}
            page={resource.data}
            query={query}
          />
        )}
      </PageState>
    </Box>
  );
}

function MemberGrid({
  columns,
  onQueryChange,
  page,
  query,
}: {
  readonly columns: readonly GridColDef<MemberSummary>[];
  readonly onQueryChange: (query: MemberListQuery) => void;
  readonly page: PageResult<MemberSummary>;
  readonly query: MemberListQuery;
}) {
  const { t } = useTranslation();
  return (
    <Box
      aria-label={t('members.gridLabel')}
      role="region"
      sx={{ maxWidth: '100%', overflowX: 'auto' }}
      tabIndex={0}
    >
      <ServerDataGrid
        columns={columns}
        disableRowSelectionOnClick
        onQueryChange={(next) => onQueryChange({ ...next, page: next.page + 1 })}
        page={page}
        query={{ ...query, page: query.page - 1 }}
        sx={{ minWidth: 1700 }}
      />
    </Box>
  );
}

function MemberFiltersForm({
  onApply,
  value,
}: {
  readonly onApply: (filters: MemberFilters) => void;
  readonly value: MemberFilters;
}) {
  const { t } = useTranslation();
  const { handleSubmit, register, reset } = useForm<MemberFilters>({ defaultValues: value });
  const activeSecondaryFilterCount = countActiveFilterValues(
    {
      uplineMemberId: value.uplineMemberId,
      partnerUserId: value.partnerUserId,
      referralCode: value.referralCode,
      memberType: value.memberType,
    },
    {
      uplineMemberId: defaultMemberListQuery.filters.uplineMemberId,
      partnerUserId: defaultMemberListQuery.filters.partnerUserId,
      referralCode: defaultMemberListQuery.filters.referralCode,
      memberType: defaultMemberListQuery.filters.memberType,
    },
  );
  return (
    <ListFilterPanel
      activeSecondaryFilterCount={activeSecondaryFilterCount}
      applyLabel={t('members.actions.applyFilters')}
      ariaLabel={t('filterPanel.label', { page: t('members.title') })}
      onClear={() => {
        reset(defaultMemberListQuery.filters);
        onApply(defaultMemberListQuery.filters);
      }}
      onSubmit={handleSubmit((filters) =>
        onApply({
          ...filters,
          partnerId: filters.partnerId.trim(),
          agentId: filters.agentId.trim(),
          uplineMemberId: filters.uplineMemberId.trim(),
          username: filters.username.trim(),
          partnerUserId: filters.partnerUserId.trim(),
          referralCode: filters.referralCode.trim().toUpperCase(),
        }),
      )}
      primaryFields={
        <>
          <TextField label={t('members.filters.username')} size="small" {...register('username')} />
          <TextField
            label={t('members.filters.partnerId')}
            size="small"
            {...register('partnerId')}
          />
          <TextField label={t('members.filters.agentId')} size="small" {...register('agentId')} />
          <TextField
            defaultValue=""
            label={t('members.filters.status')}
            select
            size="small"
            {...register('status')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="ACTIVE">ACTIVE</MenuItem>
            <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
            <MenuItem value="CLOSED">CLOSED</MenuItem>
          </TextField>
        </>
      }
      secondaryFields={
        <>
          <TextField
            label={t('members.filters.uplineId')}
            size="small"
            {...register('uplineMemberId')}
          />
          <TextField
            label={t('members.filters.partnerUserId')}
            size="small"
            {...register('partnerUserId')}
          />
          <TextField
            label={t('members.filters.referralCode')}
            size="small"
            {...register('referralCode')}
          />
          <TextField
            defaultValue=""
            label={t('members.filters.type')}
            select
            size="small"
            {...register('memberType')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="NORMAL">NORMAL</MenuItem>
            <MenuItem value="HOUSE">HOUSE</MenuItem>
          </TextField>
        </>
      }
    />
  );
}

export function MemberStatusChip({ status }: { readonly status: MemberSummary['status'] }) {
  return (
    <Chip
      color={status === 'ACTIVE' ? 'success' : status === 'SUSPENDED' ? 'warning' : 'default'}
      label={status}
      size="small"
    />
  );
}

function initialMemberListQuery(searchParams: URLSearchParams): MemberListQuery {
  const memberType = searchParams.get('memberType');
  const status = searchParams.get('status');
  return {
    ...defaultMemberListQuery,
    filters: {
      ...defaultMemberListQuery.filters,
      partnerId: searchParams.get('partnerId')?.trim() ?? '',
      agentId: searchParams.get('agentId')?.trim() ?? '',
      uplineMemberId: searchParams.get('uplineMemberId')?.trim() ?? '',
      username: searchParams.get('username')?.trim() ?? '',
      partnerUserId: searchParams.get('partnerUserId')?.trim() ?? '',
      referralCode: searchParams.get('referralCode')?.trim().toUpperCase() ?? '',
      memberType: memberType === 'NORMAL' || memberType === 'HOUSE' ? memberType : '',
      status: status === 'ACTIVE' || status === 'SUSPENDED' || status === 'CLOSED' ? status : '',
    },
  };
}
